import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Gift, ChevronRight, Loader2, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AMOUNTS = [50, 100, 150, 200, 250];

const GiftCardPage = () => {
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    sender_name: "",
    sender_email: "",
    recipient_name: "",
    recipient_email: "",
    message: ""
  });

  const finalAmount = isCustom ? parseFloat(customAmount) || 0 : selectedAmount;

  const handlePurchase = async () => {
    if (!form.sender_name || !form.sender_email || !form.recipient_name || !form.recipient_email) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (finalAmount < 25 || finalAmount > 500) {
      toast.error("Gift card amount must be between $25 and $500");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/gift-card/checkout`, {
        amount: finalAmount,
        ...form,
        origin_url: window.location.origin
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Unable to process gift card. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Gift Cards"
        description="Give the gift of beauty! Purchase a Glamour Body gift card for facials, body contouring, waxing, and more. Perfect for birthdays, holidays, and special occasions."
        keywords="beauty gift card Katy Texas, spa gift card Houston, facial gift card, body contouring gift, Glamour Body gift"
        canonical="https://glamourbodyhouston.com/gift-cards"
      />

      <div className="pt-24 min-h-screen">
        <section className="relative py-16 px-4" data-testid="gift-card-section">
          <div className="orb-purple top-20 -left-40" />
          <div className="orb-cyan top-60 -right-40" />
          <div className="orb-teal bottom-20 left-20" />

          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-italiana text-4xl sm:text-5xl text-white mb-4 tracking-wider uppercase">Gift Cards</h1>
              <p className="text-white/60 text-base lg:text-lg max-w-2xl mx-auto">Give the gift of beauty and confidence. Redeemable on any Glamour Body service.</p>
            </motion.div>

            {/* Gift Card Preview */}
            <motion.div
              className="max-w-lg mx-auto mb-12"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
            >
              <div className="relative overflow-hidden rounded-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/30 via-cyan-500/20 to-emerald-500/20" />
                <div className="relative glass-card rounded-2xl p-8 border border-purple-500/30 text-center">
                  <Gift className="mx-auto mb-4 text-purple-400" size={40} />
                  <p className="text-purple-300 text-xs uppercase tracking-widest mb-2">Glamour Body Gift Card</p>
                  <p className="font-syne text-5xl gradient-text mb-2">${finalAmount > 0 ? finalAmount : '___'}</p>
                  <p className="text-white/50 text-sm">Redeemable on any service</p>
                </div>
              </div>
            </motion.div>

            {/* Amount Selection */}
            <motion.div
              className="max-w-lg mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <h3 className="text-white font-semibold mb-4 text-center">Select Amount</h3>
              <div className="flex flex-wrap justify-center gap-3 mb-4">
                {AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => { setSelectedAmount(amount); setIsCustom(false); }}
                    className={`px-6 py-3 rounded-full border transition-all text-sm font-syne ${
                      !isCustom && selectedAmount === amount
                        ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-cyan-400 text-white'
                        : 'border-white/20 text-white/60 hover:border-white/40'
                    }`}
                    data-testid={`gift-amount-${amount}`}
                  >
                    ${amount}
                  </button>
                ))}
                <button
                  onClick={() => setIsCustom(true)}
                  className={`px-6 py-3 rounded-full border transition-all text-sm ${
                    isCustom
                      ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-cyan-400 text-white'
                      : 'border-white/20 text-white/60 hover:border-white/40'
                  }`}
                  data-testid="gift-amount-custom"
                >
                  Custom
                </button>
              </div>
              {isCustom && (
                <div className="max-w-[200px] mx-auto">
                  <Input
                    type="number"
                    min="25"
                    max="500"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="input-dark rounded-lg text-center text-lg"
                    placeholder="$25 - $500"
                    data-testid="gift-custom-amount-input"
                  />
                </div>
              )}
            </motion.div>

            {/* Purchase Button */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <Button
                onClick={() => setShowModal(true)}
                disabled={finalAmount < 25}
                className="btn-primary rounded-full px-10 py-6 text-lg"
                data-testid="gift-purchase-btn"
              >
                <Heart className="mr-2" size={20} /> Purchase Gift Card
              </Button>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Purchase Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="glass-card border-white/20 max-w-md" data-testid="gift-card-modal">
          <DialogHeader>
            <DialogTitle className="font-italiana text-2xl text-white">Send a Gift Card</DialogTitle>
            <DialogDescription className="text-white/60">
              ${finalAmount} Glamour Body Gift Card
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-white/70 text-sm mb-1">Your Name *</label>
                <Input value={form.sender_name} onChange={(e) => setForm({...form, sender_name: e.target.value})} className="input-dark rounded-lg" placeholder="Your name" data-testid="gift-sender-name" />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Your Email *</label>
                <Input type="email" value={form.sender_email} onChange={(e) => setForm({...form, sender_email: e.target.value})} className="input-dark rounded-lg" placeholder="you@email.com" data-testid="gift-sender-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-white/70 text-sm mb-1">Recipient Name *</label>
                <Input value={form.recipient_name} onChange={(e) => setForm({...form, recipient_name: e.target.value})} className="input-dark rounded-lg" placeholder="Their name" data-testid="gift-recipient-name" />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Recipient Email *</label>
                <Input type="email" value={form.recipient_email} onChange={(e) => setForm({...form, recipient_email: e.target.value})} className="input-dark rounded-lg" placeholder="them@email.com" data-testid="gift-recipient-email" />
              </div>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-1">Personal Message</label>
              <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="input-dark rounded-lg" placeholder="Add a personal note..." rows={3} data-testid="gift-message" />
            </div>

            <Button onClick={handlePurchase} disabled={isLoading} className="w-full btn-primary rounded-full py-6" data-testid="gift-checkout-btn">
              {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...</> : <>Pay ${finalAmount} <ChevronRight className="ml-2" size={18} /></>}
            </Button>

            <p className="text-white/40 text-xs text-center">
              Recipient will receive a beautiful gift card email after payment
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GiftCardPage;
